import { DataAccess } from "../../../dataaccess/data.access";
import { Cache, Invalidate } from "../../../decorators/cache.decorator";

export interface EngagementActivityType {
    activityTypeKey: string;
    instance: string;
    weight: number;
    isActive: boolean;
    createDt?: string;
    lastModDt?: string;
}

export interface EngagementActivity {
    activityId: number;
    instance: string;
    contId: number;
    activityTypeKey: string;
    organizationId?: number;
    eventId?: number;
    activityDt: string;
    metadata?: string;
}

export interface EngagementAtRiskConfig {
    instance: string;
    recentWindowDays: number;
    baselineWindowDays: number;
    dropThresholdPct: number;
}

export interface EngagementBadge {
    badgeId?: number;
    badgeKey: string;
    instance?: string;
    name: string;
    description?: string;
    badgeGroup?: string;
    tier?: number;
    isVisible: boolean;
    isActive: boolean;
    iconUrl?: string;
    createDt?: string;
    lastModDt?: string;
    criteria?: EngagementBadgeCriteria[];
}

export interface EngagementBadgeCriteria {
    criteriaId?: number;
    badgeId?: number;
    activityTypeKey: string;
    thresholdCount: number;
    timeWindowDays?: number;
}

export interface EngagementBadgeEarned {
    earnedId: number;
    contId: number;
    badgeId: number;
    instance: string;
    earnedDt: string;
    acknowledgedDt?: string;
}

export interface TimeSeriesPoint {
    period: string;
    score: number;
}

export interface OrgTimeSeriesPoint {
    organizationId: number;
    period: string;
    score: number;
}

export interface AtRiskUser {
    contId: number;
    baselineRate: number;
    recentRate: number;
    dropPct: number;
}

export interface ReportRequest {
    startDt: string;
    endDt: string;
    bucket: string;
    weekstart: number;
}

export interface AtRiskRequest {
    recentWindowDays?: number;
    baselineWindowDays?: number;
    dropThresholdPct?: number;
}

export interface ActivityResponse {
    newBadgesEarned: EngagementBadge[];
}

export interface BadgeProgressItem {
    badgeId: number;
    badgeKey: string;
    name: string;
    description?: string;
    badgeGroup?: string;
    tier?: number;
    iconUrl?: string;
    criteria: BadgeCriteriaProgress[];
}

export interface BadgeCriteriaProgress {
    activityTypeKey: string;
    current: number;
    threshold: number;
    timeWindowDays?: number;
}

/** ANG-7007 Connect25 system alerts — same JSON fields as the GDA entity. */
export interface EngagementSystemAlertAudience {
    engagementSystemAlertAudienceId?: number;
    engagementSystemAlertId?: number;
    audienceType: "ORG_ROLE" | "ROLE" | "SEC_GROUP" | "RATING";
    organizationId?: number;
    roleId?: number;
    groupId?: number;
    ratingId?: number;
}

export interface EngagementSystemAlert {
    engagementSystemAlertId?: number;
    instance?: string;
    sortOrder?: number;
    bodyHtml: string;
    uxType: "MODAL" | "BANNER";
    showToAnonymous: boolean;
    active: boolean;
    /** User-local schedule bounds (optional); omit both for always-on. Responses are ISO strings; POST/PUT may pass Date (DataAccess serializes). */
    startDt?: string | Date;
    endDt?: string | Date;
    audiences: EngagementSystemAlertAudience[];
}

export class EngagementService {
    static recordActivity(
        typeKey: string,
        orgId?: number,
        eventId?: number,
        metadata?: string,
    ): Promise<ActivityResponse> {
        const body: Record<string, unknown> = { typeKey };
        if (orgId != null) body.organizationId = orgId;
        if (eventId != null) body.eventId = eventId;
        if (metadata != null) body.metadata = metadata;
        return DataAccess.post<ActivityResponse>("/engagement/activity.json", body);
    }

    static getActivityTypes(): Promise<EngagementActivityType[]> {
        return DataAccess.get<EngagementActivityType[]>("/engagement/types.json");
    }

    static updateActivityType(typeKey: string, weight: number, isActive: boolean): Promise<void> {
        return DataAccess.put<void>(`/engagement/types/${encodeURIComponent(typeKey)}.json`, { weight, isActive });
    }

    static getEngagementOverTime(request: ReportRequest): Promise<TimeSeriesPoint[]> {
        return DataAccess.post<TimeSeriesPoint[]>("/engagement/report/over-time.json", request);
    }

    static getEngagementByOrg(request: ReportRequest): Promise<OrgTimeSeriesPoint[]> {
        return DataAccess.post<OrgTimeSeriesPoint[]>("/engagement/report/by-org.json", request);
    }

    static getAtRiskUsers(request?: AtRiskRequest): Promise<AtRiskUser[]> {
        return DataAccess.post<AtRiskUser[]>("/engagement/report/at-risk.json", request ?? {});
    }

    static getAtRiskConfig(): Promise<EngagementAtRiskConfig> {
        return DataAccess.get<EngagementAtRiskConfig>("/engagement/at-risk-config.json");
    }

    static updateAtRiskConfig(config: EngagementAtRiskConfig): Promise<void> {
        return DataAccess.put<void>("/engagement/at-risk-config.json", config);
    }

    static getUserHistory(contId: number): Promise<EngagementActivity[]> {
        return DataAccess.get<EngagementActivity[]>(`/engagement/user/${contId}/history.json`);
    }

    static getMyHistory(): Promise<EngagementActivity[]> {
        return DataAccess.get<EngagementActivity[]>("/engagement/my/history.json");
    }

    @Cache({ targetName: "EngagementService" })
    static getBadges(): Promise<EngagementBadge[]> {
        return DataAccess.get<EngagementBadge[]>("/engagement/badges.json");
    }

    @Invalidate({ serviceName: "EngagementService", methodName: "getBadges" })
    static createBadge(
        badge: Partial<EngagementBadge> & { criteria?: EngagementBadgeCriteria[] },
    ): Promise<EngagementBadge> {
        return DataAccess.post<EngagementBadge>("/engagement/badges.json", { badge, criteria: badge.criteria });
    }

    @Invalidate({ serviceName: "EngagementService", methodName: "getBadges" })
    static updateBadge(
        badgeId: number,
        badge: Partial<EngagementBadge> & { criteria?: EngagementBadgeCriteria[] },
    ): Promise<void> {
        return DataAccess.put<void>(`/engagement/badges/${badgeId}.json`, { badge, criteria: badge.criteria });
    }

    @Invalidate({ serviceName: "EngagementService", methodName: "getBadges" })
    static deleteBadge(badgeId: number): Promise<void> {
        return DataAccess.delete(`/engagement/badges/${badgeId}.json`);
    }

    static getMyBadges(): Promise<EngagementBadgeEarned[]> {
        return DataAccess.get<EngagementBadgeEarned[]>("/engagement/my/badges.json");
    }

    static getMyUnacknowledgedBadges(): Promise<EngagementBadgeEarned[]> {
        return DataAccess.get<EngagementBadgeEarned[]>("/engagement/my/badges/unacknowledged.json");
    }

    static acknowledgeBadges(badgeIds: number[]): Promise<void> {
        return DataAccess.post<void>("/engagement/my/badges/acknowledge.json", { badgeIds });
    }

    static getMyBadgeProgress(): Promise<BadgeProgressItem[]> {
        return DataAccess.get<BadgeProgressItem[]>("/engagement/my/badge-progress.json");
    }

    static getUserBadges(contId: number): Promise<EngagementBadgeEarned[]> {
        return DataAccess.get<EngagementBadgeEarned[]>(`/engagement/user/${contId}/badges.json`);
    }

    static getActiveSystemAlerts(): Promise<EngagementSystemAlert[]> {
        return DataAccess.get<EngagementSystemAlert[]>("/engagement/system-alerts/active.json");
    }

    static listSystemAlertsAdmin(): Promise<EngagementSystemAlert[]> {
        return DataAccess.get<EngagementSystemAlert[]>("/engagement/system-alerts.json");
    }

    static createSystemAlert(alert: EngagementSystemAlert): Promise<EngagementSystemAlert> {
        return DataAccess.post<EngagementSystemAlert>("/engagement/system-alerts.json", alert);
    }

    static updateSystemAlert(alertId: number, alert: EngagementSystemAlert): Promise<EngagementSystemAlert> {
        return DataAccess.put<EngagementSystemAlert>(`/engagement/system-alerts/${alertId}.json`, alert);
    }

    static deleteSystemAlert(alertId: number): Promise<void> {
        return DataAccess.delete(`/engagement/system-alerts/${alertId}.json`);
    }

    static reorderSystemAlert(alertId: number, direction: "up" | "down"): Promise<void> {
        return DataAccess.post<void>(`/engagement/system-alerts/${alertId}/reorder.json`, { direction });
    }
}
